
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Quote, ChevronLeft, ChevronRight, User, AlertCircle } from 'lucide-react';
import { useApp } from '../context/AppContext';

const Testimonials: React.FC = () => {
  const { testimonials } = useApp();
  const [index, setIndex] = useState(0);

  const next = () => {
    if (testimonials.length === 0) return;
    setIndex((prev) => (prev + 1) % testimonials.length);
  };
  
  const prev = () => {
    if (testimonials.length === 0) return;
    setIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  useEffect(() => {
    if (testimonials.length <= 1) return;
    const timer = setInterval(next, 8000);
    return () => clearInterval(timer);
  }, [testimonials.length]);

  return (
    <section className="py-24 bg-white border-t border-slate-200 relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex flex-col items-center text-center mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-50 border border-indigo-100 text-indigo-600 text-xs font-bold uppercase tracking-widest mb-6">
            Client Success
          </div>
          <h2 className="font-display font-bold text-3xl md:text-5xl text-slate-900 mb-6">Institutional Validation</h2>
          <p className="text-slate-500 max-w-2xl mx-auto text-lg">
            Trust is architected through precision. Verification of our financial stewardship from industry leaders.
          </p>
        </div>

        <div className="relative max-w-4xl mx-auto px-12 min-h-[400px]">
          {testimonials.length > 0 ? (
            <>
              <AnimatePresence mode="wait">
                <motion.div
                  key={testimonials[index]?.id || 'empty'}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 1.05 }}
                  transition={{ duration: 0.5 }}
                  className="bg-white border border-slate-200 rounded-[40px] p-8 md:p-16 text-center relative shadow-2xl"
                >
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 w-20 h-20 bg-indigo-600 rounded-2xl flex items-center justify-center shadow-xl ring-4 ring-white">
                    <Quote className="text-white" size={40} />
                  </div>
                  
                  <p className="text-xl md:text-2xl text-slate-700 font-medium leading-relaxed mb-12 italic">
                    "{testimonials[index]?.content}"
                  </p>
                  
                  <div className="flex flex-col items-center">
                    <div className="w-16 h-16 rounded-full bg-indigo-50 flex items-center justify-center mb-4 border border-indigo-100">
                      <User className="text-indigo-600" size={32} />
                    </div>
                    <h4 className="text-slate-900 font-bold text-lg">{testimonials[index]?.name}</h4>
                    <p className="text-slate-500 font-medium uppercase tracking-wider text-xs mt-1">
                      {testimonials[index]?.role}
                    </p>
                  </div>
                </motion.div>
              </AnimatePresence>

              {/* Controls */}
              {testimonials.length > 1 && (
                <>
                  <button 
                    onClick={prev}
                    className="absolute left-0 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white border border-slate-200 flex items-center justify-center text-slate-400 hover:text-white hover:bg-indigo-600 hover:border-indigo-600 transition-all z-10 shadow-lg"
                  >
                    <ChevronLeft size={24} />
                  </button>
                  <button 
                    onClick={next}
                    className="absolute right-0 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white border border-slate-200 flex items-center justify-center text-slate-400 hover:text-white hover:bg-indigo-600 hover:border-indigo-600 transition-all z-10 shadow-lg"
                  >
                    <ChevronRight size={24} />
                  </button>
                </>
              )}
              
              <div className="flex justify-center gap-3 mt-12">
                {testimonials.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setIndex(i)}
                    className={`h-1.5 rounded-full transition-all ${index === i ? 'w-8 bg-indigo-600' : 'w-2 bg-slate-300 hover:bg-slate-400'}`}
                  />
                ))}
              </div>
            </>
          ) : (
            <div className="bg-slate-50 border border-dashed border-slate-300 rounded-[40px] p-20 text-center">
              <AlertCircle className="mx-auto text-slate-400 mb-4" size={48} />
              <p className="text-slate-500 font-bold uppercase tracking-widest text-sm">Success records pending deployment.</p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
